import {
    authSlice,
    initialState,
    type PostLoginResponse,
} from '@starjack/features/authentication';
import {
    postRefresh,
    usePostLoginMutation,
    USER_AUTH_CACHE,
} from '@/api/v2/authentication';
import jwt_decode from 'jwt-decode';
import { Permission, UserType } from '@/models/user';
import { getUnixTime, isAfter } from 'date-fns';
import {
    deleteFromLocalStorage,
    getFromLocalStorage,
    saveToLocalStorage,
} from '@/utils';
import { LOCAL_STORAGE_KEY_AUTH } from '@/constants';

import { useSlice } from '@/hooks/useSlice';
import { isOfTypeBackendError } from '@/utils/guards';

const prepareAuthStoreData = (data: PostLoginResponse, username: string) => {
    const { jwt, refreshCode, validFor } = data;

    const decoded: {
        sub: string;
        iat: number;
        g: [UserType, string?];
        p: Permission[];
    } = jwt_decode(jwt);

    const [userType, lOId] = decoded.g;

    const userId = decoded.sub;

    let liftOperatorId = decoded.sub;
    if (userType === UserType.LiftOperatorUser) {
        liftOperatorId = lOId.split(':')[1];
    }

    const tokenExpiresAt = getUnixTime(new Date()) + validFor - 5;

    let permissions: Permission[];

    if (userType === UserType.LiftOperator) {
        permissions = [Permission.LIFTOPERATOR];
    } else if (userType === UserType.Supporter) {
        permissions = [Permission.SUPPORTER];
    } else {
        permissions = [Permission.ADMIN];
    }

    return {
        validFor,
        refreshCode,
        permissions: permissions,
        tokenExpiresAt,
        jwt,
        userId,
        userType,
        liftOperatorData: {
            skiOperator: username,
            liftOperatorId,
        },
    };
};

// One timer for the whole app: logout must stop it, and a new login must not
// start a second chain racing the first one for the refresh code.
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

const scheduleRefresh = (refresh: () => void, ms: number) => {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
        refreshTimer = null;
        refresh();
    }, ms);
};

export const stopRefresh = () => {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = null;
};

export const useAuthentication = () => {
    const [postLogin, authRequestData] = usePostLoginMutation({
        fixedCacheKey: USER_AUTH_CACHE,
    });

    const {
        state: authStore,
        actions: { setAuthData, clearAuthData },
    } = useSlice(authSlice, 'authSlice');

    const refreshToken = async (
        refreshCode: string,
        username: string
    ): Promise<
        [
            authData: ReturnType<typeof prepareAuthStoreData>,
            error: 'refresh_failed' | null,
        ]
    > => {
        try {
            const data = await postRefresh(refreshCode);
            const storeData = prepareAuthStoreData(data, username);

            setAuthData({
                ...storeData,
                permissions:
                    storeData.permissions as unknown as import('@starjack/features/authentication').Permission[],
                userType:
                    storeData.userType as unknown as import('@starjack/features/authentication').UserType,
            });
            saveToLocalStorage(LOCAL_STORAGE_KEY_AUTH, storeData);

            scheduleRefresh(
                () => refreshToken(storeData.refreshCode, username),
                (storeData.validFor - 180) * 1000
            );

            return [storeData, null];
        } catch (e) {
            console.error(e);

            clearAuthData({ userLogout: false });

            return [null, 'refresh_failed'];
        }
    };

    const login = async (
        username: string,
        password: string
    ): Promise<[success: boolean, errorMessage: string]> => {
        try {
            const data = await postLogin({
                username,
                password,
            }).unwrap();
            const storeData = prepareAuthStoreData(data, username);

            setAuthData({
                ...storeData,
                permissions:
                    storeData.permissions as unknown as import('@starjack/features/authentication').Permission[],
                userType:
                    storeData.userType as unknown as import('@starjack/features/authentication').UserType,
            });
            saveToLocalStorage(LOCAL_STORAGE_KEY_AUTH, storeData);

            // set time out to refresh data
            scheduleRefresh(
                () => refreshToken(storeData.refreshCode, username),
                (storeData.validFor - 180) * 1000
            );

            return [true, null];
        } catch (e) {
            console.error(e);

            if (e.data && isOfTypeBackendError(e.data)) {
                if (e.status === 401) {
                    return [false, 'bad_credentials'];
                }
            }

            return [false, 'general'];
        }
    };

    const logout = () => {
        stopRefresh();
        deleteFromLocalStorage(LOCAL_STORAGE_KEY_AUTH);
        clearAuthData({
            userLogout: true,
        });
    };

    const isLoggedIn = () => {
        if (!authStore.jwt) {
            return false;
        }

        const { tokenExpiresAt } = authStore;

        if (!tokenExpiresAt) return false;

        return !isAfter(new Date(), new Date(tokenExpiresAt * 1000));
    };

    const getAuthDataFromLocalStorage = () => {
        const authState = getFromLocalStorage(
            LOCAL_STORAGE_KEY_AUTH,
            initialState
        );

        if (!!authState.tokenExpiresAt && !!authState.jwt) {
            if (
                isAfter(new Date(), new Date(authState.tokenExpiresAt * 1000))
            ) {
                setAuthData(initialState);
                return false;
            }

            const timeLeft = authState.tokenExpiresAt - getUnixTime(new Date());

            scheduleRefresh(
                () =>
                    refreshToken(
                        authState.refreshCode,
                        authState.liftOperatorData.skiOperator
                    ),
                (timeLeft - 180) * 1000
            );
        }

        setAuthData(authState);
        return true;
    };

    return {
        authRequestData,
        isLoggedIn,
        getAuthDataFromLocalStorage,
        login,
        logout,
        ...authStore,
    };
};
